<!-- Hero Section -->
<section class="relative h-[60vh] md:h-[70vh] w-full overflow-hidden">
	<!-- Gradient Background -->
	<div class="absolute inset-0 bg-linear-to-br from-charcoal via-primary to-charcoal"></div>

	<!-- Decorative Elements -->
	<div class="absolute inset-0 overflow-hidden pointer-events-none">
		<div class="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-secondary/15 rounded-full blur-3xl animate-pulse"></div>
		<div class="absolute bottom-1/4 -right-32 w-[600px] h-[600px] bg-secondary/10 rounded-full blur-3xl animate-pulse" style="animation-delay: 1.5s;"></div>
		<div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-white/5 rounded-full blur-3xl"></div>
	</div>

	<!-- Grid Pattern -->
	<div class="absolute inset-0 opacity-10" style="background-image: linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px); background-size: 60px 60px;"></div>

	<!-- Content - Centered -->
	<div class="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 md:px-16 lg:px-20">
		<!-- Decorative Line -->
		<div class="flex items-center gap-4 mb-8">
			<div class="w-16 h-px bg-linear-to-r from-transparent to-secondary"></div>
			<span class="text-secondary text-sm font-semibold tracking-[0.3em] uppercase">Our Blog</span>
			<div class="w-16 h-px bg-linear-to-l from-transparent to-secondary"></div>
		</div>

		<!-- Main Heading -->
		<h1 class="font-cenzo text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[0.95] mb-6">
			Stories
		</h1>

		<!-- Description -->
		<p class="text-white/70 text-lg md:text-xl max-w-2xl leading-relaxed">
			Insights, tips, and inspiration for your hair journey
		</p>
	</div>
</section>

<!-- Search & Filter Bar -->
<section class="border-y border-charcoal/10 bg-white sticky top-[72px] z-30">
	<div class="max-w-6xl mx-auto px-6">
		<div class="flex flex-col md:flex-row md:items-center md:justify-between md:h-14 py-3 md:py-0 gap-3 md:gap-0">
			<div class="flex items-center gap-6 font-bold text-lg overflow-x-auto scrollbar-hide">
				@for (tag of tagOptions; track tag) {
				<button
					(click)="onTagChange(tag)"
					[class.text-charcoal]="selectedTag() === tag"
					[class.font-semibold]="selectedTag() === tag"
					[class.text-charcoal/40]="selectedTag() !== tag"
					class="transition-colors hover:text-charcoal whitespace-nowrap"
				>
					{{ tag }}
				</button>
				}
			</div>
			
			<div class="relative">
				<input
					type="text"
					placeholder="Search"
					(input)="onSearch($event)"
					class="w-full md:w-56 px-4 py-2 pl-9 text-md font-semibold bg-transparent border-b border-charcoal/20 focus:border-charcoal focus:outline-none transition-colors"
				/>
				<svg class="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
				</svg>
			</div>
		</div>
	</div>
</section>

<!-- Main Content -->
<section class="py-16 md:py-20 bg-neutral-50 min-h-screen">
	<div class="max-w-6xl mx-auto px-6">
		
		<!-- Loading -->
		@if (isLoading()) {
		<div class="flex justify-center py-20">
			<div class="w-8 h-8 border-2 border-charcoal/20 border-t-charcoal rounded-full animate-spin"></div>
		</div>
		}

		<!-- Error -->
		@else if (errorMessage()) {
		<div class="text-center py-20 text-red-600">{{ errorMessage() }}</div>
		}

		<!-- Empty -->
		@else if (blogs().length === 0 && featuredBlogs().length === 0) {
		<div class="text-center py-20">
			<p class="text-charcoal/60">No stories yet. Check back soon!</p>
		</div>
		}

		@else {
		
		<!-- Featured Story -->
		@if (featuredBlogs().length > 0) {
		<a [routerLink]="['/blog', featuredBlogs()[0].slug]" class="block mb-16 group">
			<div class="grid lg:grid-cols-2 gap-8 items-center">
				<div class="overflow-hidden">
					<img
						[src]="getImageUrl(featuredBlogs()[0].featured_image_url)"
						[alt]="featuredBlogs()[0].title"
						class="w-full aspect-4/3 object-cover transition-transform duration-700 group-hover:scale-105"
						(error)="onImageError($event)"
					/>
				</div>
				<div class="lg:pl-8">
					<span class="text-xs font-semibold text-charcoal/50 uppercase tracking-wider">Featured</span>
					<h2 class="font-cenzo text-3xl md:text-4xl lg:text-5xl font-bold text-charcoal mt-3 mb-4 leading-tight group-hover:text-primary transition-colors">
						{{ featuredBlogs()[0].title }}
					</h2>
					<p class="text-charcoal/60 text-base md:text-lg mb-6 line-clamp-3">
						{{ featuredBlogs()[0].short_description }}
					</p>
					<div class="flex items-center gap-4 text-md font-semibold text-charcoal/50">
						<span>{{ formatDate(featuredBlogs()[0].published_at || featuredBlogs()[0].created_at) }}</span>
						<span class="w-1 h-1 bg-charcoal/30 rounded-full"></span>
						<span>{{ featuredBlogs()[0].reading_time_minutes }} min read</span>
					</div>
				</div>
			</div>
		</a>
		
		<hr class="border-charcoal/10 mb-16">
		}

		<!-- Stories Grid -->
		<div class="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
			@for (blog of blogs(); track blog.id) {
			<a [routerLink]="['/blog', blog.slug]" class="block group">
				<article>
					<div class="overflow-hidden mb-5">
						<img
							[src]="getImageUrl(blog.featured_image_url)"
							[alt]="blog.title"
							class="w-full aspect-3/2 object-cover transition-transform duration-700 group-hover:scale-105"
							(error)="onImageError($event)"
						/>
					</div>
					<div class="flex items-center gap-3 text-md font-semibold text-charcoal/50 mb-3">
						<span>{{ formatDate(blog.published_at || blog.created_at) }}</span>
						<span class="w-1 h-1 bg-charcoal/30 rounded-full"></span>
						<span>{{ blog.reading_time_minutes }} min read</span>
					</div>
					<h3 class="font-cenzo text-xl md:text-2xl font-bold text-charcoal mb-3 leading-snug group-hover:text-primary transition-colors">
						{{ blog.title }}
					</h3>
					<p class="text-charcoal/60 text-md line-clamp-2">
						{{ blog.short_description }}
					</p>
				</article>
			</a>
			}
		</div>

		<!-- Load More -->
		@if (hasMore()) {
		<div class="text-center mt-16">
			<button 
				(click)="loadMore()"
				[disabled]="isLoadingMore()"
				class="relative px-7 py-3 lg:px-9 lg:py-3.5 rounded-lg font-bold text-base lg:text-lg tracking-wider overflow-hidden border-2 border-charcoal/30 text-primary transition-all duration-300 btn-slide disabled:opacity-50 disabled:cursor-not-allowed">
				<span class="relative z-10 flex items-center justify-center gap-2">
					@if (isLoadingMore()) {
						<span class="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
						<span>LOADING...</span>
					} @else {
						LOAD MORE
					}
				</span>
			</button>
		</div>
		}
		
		}
	</div>
</section>
